import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import moment from 'moment-timezone';
import DialogContainer from 'components/DialogContainer/styles';
import DialogClose from 'components/DialogClose/styles';
import {
  DeliveredAt,
  PhotoArea,
  PhotoFrame,
  PhotoUnavailable,
  PhotoViewport,
  ProofOfDeliveryHeader,
  ProofOfDeliveryTitle,
  ZoomButton,
  ZoomControls
} from './styles';

const ZOOM_FIT = 1;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.5;

const DEFAULT_TIMEZONE_ID = 'America/New_York';

// IANA timezone keeps DST correct for the delivery date, unlike a fixed offset.
const formatDeliveredAt = (deliveredAt, timezoneId) => {
  if (!deliveredAt) return null;
  const date = moment.utc(deliveredAt);
  if (!date.isValid()) return null;
  return date
    .tz(timezoneId || DEFAULT_TIMEZONE_ID)
    .format('MMMM D, YYYY [at] h:mm A');
};

const ProofOfDeliveryModal = ({
  isOpen,
  onClose,
  proofOfDelivery,
  timezoneId
}) => {
  const photoUrl = proofOfDelivery?.photoUrl;
  const [photoFailed, setPhotoFailed] = useState(false);
  const [zoom, setZoom] = useState(ZOOM_FIT);

  useEffect(() => {
    setPhotoFailed(false);
    setZoom(ZOOM_FIT);
  }, [photoUrl, isOpen]);

  const deliveredAt = formatDeliveredAt(
    proofOfDelivery?.deliveredAt,
    timezoneId
  );
  const showPhoto = photoUrl && !photoFailed;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogContainer>
        <DialogClose onClick={onClose} />
        <ProofOfDeliveryHeader>
          <ProofOfDeliveryTitle>Order delivered</ProofOfDeliveryTitle>
          {deliveredAt && (
            <DeliveredAt>
              Delivered <strong>{deliveredAt}</strong>
            </DeliveredAt>
          )}
        </ProofOfDeliveryHeader>
        <PhotoArea>
          <PhotoViewport>
            <PhotoFrame zoom={zoom}>
              {showPhoto ? (
                <img
                  src={photoUrl}
                  alt='Proof of delivery'
                  referrerPolicy='no-referrer'
                  onError={() => setPhotoFailed(true)}
                />
              ) : (
                <PhotoUnavailable>
                  The delivery photo could not be loaded.
                </PhotoUnavailable>
              )}
            </PhotoFrame>
          </PhotoViewport>
          {showPhoto && (
            <ZoomControls>
              <ZoomButton
                type='button'
                aria-label='Zoom out'
                disabled={zoom <= ZOOM_MIN}
                onClick={() => setZoom((value) => value - ZOOM_STEP)}
              >
                &minus;
              </ZoomButton>
              <ZoomButton
                type='button'
                aria-label='Zoom in'
                disabled={zoom >= ZOOM_MAX}
                onClick={() => setZoom((value) => value + ZOOM_STEP)}
              >
                +
              </ZoomButton>
            </ZoomControls>
          )}
        </PhotoArea>
      </DialogContainer>
    </Dialog>
  );
};

ProofOfDeliveryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  proofOfDelivery: PropTypes.shape({
    photoUrl: PropTypes.string,
    deliveredAt: PropTypes.string
  }),
  timezoneId: PropTypes.string
};

ProofOfDeliveryModal.defaultProps = {
  proofOfDelivery: null,
  timezoneId: DEFAULT_TIMEZONE_ID
};

export default ProofOfDeliveryModal;
